/**
 * Apple Receipt Validation Service
 * 
 * PHASE 2.3: Enhanced Receipt Validation Implementation
 * 
 * Provides comprehensive Apple receipt validation using the App Store API
 * with cordova-plugin-purchase integration. Handles receipt validation,
 * retry logic, validation logging, and integration with cross-platform
 * transaction storage.
 * 
 * Key Features:
 * - App Store API receipt validation
 * - Retry logic with exponential backoff
 * - Receipt validation logging for audit trails
 * - Deduplication using receipt hashes
 * - Integration with cross-platform transaction database
 * - Support for both sandbox and production environments
 * - Receipt refresh scenarios handling
 * 
 * Security:
 * - Receipt hash validation for deduplication
 * - Environment validation (sandbox vs production)
 * - Secure receipt data handling
 * - Validation response logging for audit
 * 
 * Integration:
 * - Works with IOSInAppPurchaseService for receipt processing
 * - Uses CrossPlatformTransactionService for database operations
 * - Integrates with ios_receipt_validation_log table
 * - Supports App Store Server Notifications V2 webhook
 */

import { Capacitor } from '@capacitor/core';
import { isIOSNativeApp } from '@/utils/platformUtils';
import { crossPlatformTransactionService } from '@/services/database/CrossPlatformTransactionService';
import type { IOSReceiptValidationLog } from '@/types/crossPlatformTransactions';
import { supabase } from '@/integrations/supabase/client';

/**
 * Apple receipt validation environment
 */
export type ValidationEnvironment = 'sandbox' | 'production';

/**
 * Receipt validation status
 */
export type ValidationStatus = 'success' | 'failed' | 'pending' | 'invalid';

/**
 * Apple receipt validation request
 */
export interface ReceiptValidationRequest {
  readonly receiptData: string; // Base64-encoded receipt data
  readonly password?: string; // App-specific shared secret (for auto-renewable subscriptions)
  readonly excludeOldTransactions?: boolean; // Exclude old transactions from response
}

/**
 * Apple receipt validation response
 */
export interface ReceiptValidationResponse {
  readonly status: number; // Status code from Apple
  readonly environment: ValidationEnvironment;
  readonly receipt?: AppleReceipt;
  readonly latest_receipt?: string; // Updated receipt data
  readonly latest_receipt_info?: AppleTransaction[];
  readonly pending_renewal_info?: AppleRenewalInfo[];
  readonly is_retryable?: boolean; // Whether the request can be retried
}

/**
 * Apple receipt structure
 */
export interface AppleReceipt {
  readonly receipt_type: 'ProductionSandbox' | 'Production';
  readonly adam_id: number;
  readonly app_item_id: number;
  readonly bundle_id: string;
  readonly application_version: string;
  readonly download_id: number;
  readonly version_external_identifier: number;
  readonly receipt_creation_date: string;
  readonly receipt_creation_date_ms: string;
  readonly receipt_creation_date_pst: string;
  readonly request_date: string;
  readonly request_date_ms: string;
  readonly request_date_pst: string;
  readonly original_purchase_date: string;
  readonly original_purchase_date_ms: string;
  readonly original_purchase_date_pst: string;
  readonly original_application_version: string;
  readonly in_app: AppleTransaction[];
}

/**
 * Apple transaction info
 */
export interface AppleTransaction {
  readonly quantity: string;
  readonly product_id: string;
  readonly transaction_id: string;
  readonly original_transaction_id: string;
  readonly purchase_date: string;
  readonly purchase_date_ms: string;
  readonly purchase_date_pst: string;
  readonly original_purchase_date: string;
  readonly original_purchase_date_ms: string;
  readonly original_purchase_date_pst: string;
  readonly expires_date?: string;
  readonly expires_date_ms?: string;
  readonly expires_date_pst?: string;
  readonly web_order_line_item_id?: string;
  readonly is_trial_period?: string;
  readonly is_in_intro_offer_period?: string;
  readonly is_upgraded?: string;
  readonly cancellation_date?: string;
  readonly cancellation_date_ms?: string;
  readonly cancellation_date_pst?: string;
  readonly cancellation_reason?: string;
}

/**
 * Apple renewal info
 */
export interface AppleRenewalInfo {
  readonly original_transaction_id: string;
  readonly product_id: string;
  readonly auto_renew_status: string;
  readonly auto_renew_product_id: string;
  readonly expiration_intent?: string;
  readonly grace_period_expires_date?: string;
  readonly grace_period_expires_date_ms?: string;
  readonly grace_period_expires_date_pst?: string;
  readonly is_in_billing_retry_period?: string;
  readonly offer_code_ref_name?: string;
  readonly price_increase_status?: string;
  readonly promotional_offer_id?: string;
}

/**
 * Receipt validation result
 */
export interface ReceiptValidationResult {
  readonly success: boolean;
  readonly status: ValidationStatus;
  readonly environment: ValidationEnvironment;
  readonly receiptHash: string;
  readonly validationResponse?: ReceiptValidationResponse;
  readonly error?: string;
  readonly retryAfter?: number; // Seconds to wait before retry
  readonly logId?: string; // ID of the validation log entry
}

/**
 * Retry configuration
 */
interface RetryConfig {
  readonly maxRetries: number;
  readonly baseDelay: number; // Base delay in milliseconds
  readonly maxDelay: number; // Maximum delay in milliseconds
  readonly backoffMultiplier: number;
}

/**
 * Apple Receipt Validation Service
 */
export class AppleReceiptValidationService {
  private readonly isIOSNative: boolean;
  private readonly retryConfig: RetryConfig;
  private readonly validationCache = new Map<string, ReceiptValidationResult>();

  constructor() {
    this.isIOSNative = isIOSNativeApp();
    this.retryConfig = {
      maxRetries: 3,
      baseDelay: 1000, // 1 second
      maxDelay: 30000, // 30 seconds
      backoffMultiplier: 2
    };

    console.log('🍎 Apple Receipt Validation Service: Initialized', {
      isIOSNative: this.isIOSNative,
      platform: Capacitor.getPlatform()
    });
  }

  /**
   * Check if receipt validation is available
   */
  isAvailable(): boolean {
    return this.isIOSNative;
  }

  /**
   * Validate receipt with Apple's servers
   */
  async validateReceipt(
    receiptData: string,
    userId: string,
    transactionId?: string,
    options: {
      password?: string;
      excludeOldTransactions?: boolean;
      retryOnFailure?: boolean;
    } = {}
  ): Promise<ReceiptValidationResult> {
    if (!this.isAvailable()) {
      console.log('🚫 Apple Receipt Validation: Not available on this platform');
      return {
        success: false,
        status: 'failed',
        environment: 'production',
        receiptHash: '',
        error: 'Receipt validation not available on this platform'
      };
    }

    console.log('🔍 Apple Receipt Validation: Starting validation...', {
      userId,
      transactionId,
      hasPassword: !!options.password,
      excludeOldTransactions: options.excludeOldTransactions
    });

    // Generate receipt hash for deduplication
    const receiptHash = await this.generateReceiptHash(receiptData);

    // Check cache first
    const cachedResult = this.validationCache.get(receiptHash);
    if (cachedResult && cachedResult.success) {
      console.log('✅ Apple Receipt Validation: Using cached result');
      return cachedResult;
    }

    // Check if we've already validated this receipt recently
    const existingLog = await this.checkExistingValidation(receiptHash, userId);
    if (existingLog && existingLog.validation_status === 'success') {
      console.log('✅ Apple Receipt Validation: Found existing successful validation');
      return {
        success: true,
        status: 'success',
        environment: existingLog.validation_environment as ValidationEnvironment,
        receiptHash,
        logId: existingLog.id
      };
    }

    // Perform validation with retry logic
    const result = await this.performValidationWithRetry(
      receiptData,
      userId,
      transactionId,
      receiptHash,
      options
    );

    // Cache successful results
    if (result.success) {
      this.validationCache.set(receiptHash, result);
    }

    return result;
  }

  /**
   * Generate receipt hash for deduplication
   */
  private async generateReceiptHash(receiptData: string): Promise<string> {
    try {
      // Use Web Crypto API to generate SHA-256 hash
      const encoder = new TextEncoder();
      const data = encoder.encode(receiptData);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.error('❌ Apple Receipt Validation: Failed to generate hash:', error);
      // Fallback to simple hash
      return btoa(receiptData).slice(0, 32);
    }
  }

  /**
   * Check for existing validation in database
   */
  private async checkExistingValidation(
    receiptHash: string,
    userId: string
  ): Promise<IOSReceiptValidationLog | null> {
    try {
      const logs = await crossPlatformTransactionService.getIOSReceiptValidationLogs(
        userId,
        { receiptHash, limit: 1 }
      );
      return logs.length > 0 ? logs[0] : null;
    } catch (error) {
      console.error('❌ Apple Receipt Validation: Failed to check existing validation:', error);
      return null;
    }
  }

  /**
   * Perform validation with retry logic
   */
  private async performValidationWithRetry(
    receiptData: string,
    userId: string,
    transactionId: string | undefined,
    receiptHash: string,
    options: {
      password?: string;
      excludeOldTransactions?: boolean;
      retryOnFailure?: boolean;
    }
  ): Promise<ReceiptValidationResult> {
    let lastError: string | undefined;
    let retryCount = 0;

    while (retryCount <= this.retryConfig.maxRetries) {
      try {
        console.log(`🔄 Apple Receipt Validation: Attempt ${retryCount + 1}/${this.retryConfig.maxRetries + 1}`);

        const result = await this.performSingleValidation(
          receiptData,
          userId,
          transactionId,
          receiptHash,
          retryCount,
          options
        );

        if (result.success || !options.retryOnFailure) {
          return result;
        }

        lastError = result.error;

        // Check if we should retry based on Apple's response
        if (result.validationResponse && !result.validationResponse.is_retryable) {
          console.log('🚫 Apple Receipt Validation: Non-retryable error, stopping retries');
          break;
        }

      } catch (error) {
        console.error(`❌ Apple Receipt Validation: Attempt ${retryCount + 1} failed:`, error);
        lastError = error instanceof Error ? error.message : 'Unknown error';
      }

      retryCount++;

      if (retryCount <= this.retryConfig.maxRetries) {
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffMultiplier, retryCount - 1),
          this.retryConfig.maxDelay
        );
        console.log(`⏳ Apple Receipt Validation: Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    // All retries failed
    console.error('❌ Apple Receipt Validation: All retries failed');
    return {
      success: false,
      status: 'failed',
      environment: 'production',
      receiptHash,
      error: lastError || 'Validation failed after all retries'
    };
  }

  /**
   * Perform single validation attempt
   */
  private async performSingleValidation(
    receiptData: string,
    userId: string,
    transactionId: string | undefined,
    receiptHash: string,
    retryCount: number,
    options: {
      password?: string;
      excludeOldTransactions?: boolean;
    }
  ): Promise<ReceiptValidationResult> {
    // Log validation attempt
    const logId = await this.logValidationAttempt(
      userId,
      transactionId,
      receiptData,
      receiptHash,
      retryCount,
      'pending'
    );

    try {
      // Try production environment first
      let validationResponse = await this.callAppleValidationAPI(
        receiptData,
        userId,
        'production',
        options
      );

      // If production returns sandbox receipt error, try sandbox
      if (validationResponse.status === 21007) {
        console.log('🧪 Apple Receipt Validation: Switching to sandbox environment');
        validationResponse = await this.callAppleValidationAPI(
          receiptData,
          userId,
          'sandbox',
          options
        );
      }

      const success = validationResponse.status === 0;
      const status: ValidationStatus = success ? 'success' : 'failed';

      // Update validation log
      await this.updateValidationLog(logId, status, validationResponse);

      const result: ReceiptValidationResult = {
        success,
        status,
        environment: validationResponse.environment,
        receiptHash,
        validationResponse,
        logId,
        error: success ? undefined : `Apple validation failed with status ${validationResponse.status}`
      };

      console.log(`${success ? '✅' : '❌'} Apple Receipt Validation: ${success ? 'Success' : 'Failed'}`, {
        status: validationResponse.status,
        environment: validationResponse.environment
      });

      return result;

    } catch (error) {
      console.error('❌ Apple Receipt Validation: API call failed:', error);
      
      // Update validation log with error
      await this.updateValidationLog(logId, 'failed', undefined, error instanceof Error ? error.message : 'Unknown error');

      return {
        success: false,
        status: 'failed',
        environment: 'production',
        receiptHash,
        error: error instanceof Error ? error.message : 'Unknown error',
        logId
      };
    }
  }

  /**
   * Call Apple's receipt validation API via our server endpoint
   */
  private async callAppleValidationAPI(
    receiptData: string,
    userId: string,
    environment: ValidationEnvironment,
    options: {
      password?: string;
      excludeOldTransactions?: boolean;
    }
  ): Promise<ReceiptValidationResponse> {
    console.log('🔄 Apple Receipt Validation: Calling server validation endpoint...', {
      environment,
      hasPassword: !!options.password,
      excludeOldTransactions: options.excludeOldTransactions
    });

    try {
      // Call our Supabase Edge Function for receipt validation
      const { data, error } = await supabase.functions.invoke('validate-ios-receipt', {
        body: {
          receiptData,
          userId,
          excludeOldTransactions: options.excludeOldTransactions
        }
      });

      if (error) {
        console.error('❌ Server validation error:', error);
        throw new Error(`Server validation failed: ${error.message}`);
      }

      if (!data) {
        throw new Error('No response data from server validation');
      }

      // Transform server response to our expected format
      const serverResponse = data as {
        success: boolean;
        status: number;
        environment: 'production' | 'sandbox';
        receiptHash: string;
        error?: string;
        validationResponse?: {
          status: number;
          environment: 'Sandbox' | 'Production';
          receipt?: {
            receipt_type: string;
            bundle_id: string;
            in_app: Array<unknown>;
          };
          latest_receipt_info?: Array<unknown>;
          pending_renewal_info?: Array<unknown>;
          is_retryable?: boolean;
        };
        logId?: string;
      };

      if (!serverResponse.success) {
        console.error('❌ Apple validation failed:', serverResponse.error);
        return {
          status: serverResponse.status,
          environment: serverResponse.environment,
          receipt: undefined,
          latest_receipt_info: [],
          pending_renewal_info: [],
          is_retryable: serverResponse.validationResponse?.is_retryable || false
        };
      }

      // Return successful validation response
      const appleResponse = serverResponse.validationResponse;
      if (!appleResponse) {
        throw new Error('Missing validation response from server');
      }

      return {
        status: appleResponse.status,
        environment: serverResponse.environment,
        receipt: appleResponse.receipt as AppleReceipt | undefined,
        latest_receipt_info: (appleResponse.latest_receipt_info || []) as AppleTransaction[],
        pending_renewal_info: (appleResponse.pending_renewal_info || []) as AppleRenewalInfo[],
        is_retryable: appleResponse.is_retryable || false
      };

    } catch (error) {
      console.error('❌ Receipt validation API call failed:', error);

      // Return error response that indicates retry might be possible
      return {
        status: -1, // Custom error status
        environment,
        receipt: undefined,
        latest_receipt_info: [],
        pending_renewal_info: [],
        is_retryable: true // Allow retry for network errors
      };
    }
  }

  /**
   * Log validation attempt to database
   */
  private async logValidationAttempt(
    userId: string,
    transactionId: string | undefined,
    _receiptData: string,
    receiptHash: string,
    retryCount: number,
    status: ValidationStatus,
    errorDescription?: string
  ): Promise<string> {
    try {
      const log = await crossPlatformTransactionService.logIOSReceiptValidation({
        userId,
        transactionId,
        receiptData: _receiptData,
        receiptHash,
        validationEnvironment: 'production', // Will be updated after validation
        validationStatus: status,
        retryCount,
        errorDescription
      });
      return log.id;
    } catch (error) {
      console.error('❌ Apple Receipt Validation: Failed to log validation attempt:', error);
      throw error;
    }
  }

  /**
   * Update validation log with results
   */
  private async updateValidationLog(
    logId: string,
    status: ValidationStatus,
    validationResponse?: ReceiptValidationResponse,
    errorDescription?: string
  ): Promise<void> {
    try {
      await crossPlatformTransactionService.updateIOSReceiptValidationLog(logId, {
        validationStatus: status,
        validationEnvironment: validationResponse?.environment || 'production',
        validationResponse: validationResponse ? JSON.stringify(validationResponse) : undefined,
        errorCode: validationResponse?.status?.toString(),
        errorDescription
      });
    } catch (error) {
      console.error('❌ Apple Receipt Validation: Failed to update validation log:', error);
    }
  }

  /**
   * Clear validation cache
   */
  clearCache(): void {
    this.validationCache.clear();
    console.log('🧹 Apple Receipt Validation: Cache cleared');
  }

  /**
   * Get validation cache size
   */
  getCacheSize(): number {
    return this.validationCache.size;
  }
}

// Export singleton instance
export const appleReceiptValidationService = new AppleReceiptValidationService();
